import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemContent,
  Option,
  Select,
  Stack,
  Typography
} from '@mui/joy';

// Define types for our chat data
interface ChatMessage {
  id: string;
  sender: 'user' | 'ai';
  content: string;
  timestamp: string;
}

interface Chat {
  id: string;
  title: string;
  lastUpdated: string;
  messages: ChatMessage[];
}

// Suggested prompts
const suggestedPrompts = [
  'How do I create a new mapping mission?',
  'Show flight logs for the last campaign.',
  'Create a new drone mission to scan a 100-acre field.',
  'Generate a compliance report for all missions in April.'
];

// No props needed for this component at the moment
const MaverickAIChat = () => {
  const { t } = useTranslation();

  // State for managing chats
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Function to update messages for a specific chat
  const updateChatMessages = (chatId: string, updatedMessages: ChatMessage[]) => {
    setChats(prevChats =>
      prevChats.map(chat =>
        chat.id === chatId
          ? {
              ...chat,
              messages: updatedMessages,
              lastUpdated: new Date().toISOString()
            }
          : chat
      )
    );
  };

  // Function to handle creating a new chat
  const handleNewChat = (initialPrompt?: string) => {
    const newChatId = `chat-${Date.now()}`;

    // Create a new chat without a title initially
    // The title will be inferred from the first message
    const newChat: Chat = {
      id: newChatId,
      title: '', // Empty title initially
      lastUpdated: new Date().toISOString(),
      messages: []
    };

    // Add the new chat to the list
    setChats(prevChats => [...prevChats, newChat]);

    // Select the new chat
    setSelectedChatId(newChatId);
    setMessages([]);
    setShowSuggestions(true);

    // If there's an initial prompt, process it immediately
    if (initialPrompt) {
      // First, create a user message with the initial prompt
      const newUserMessage: ChatMessage = {
        id: `msg-${Date.now()}-user`,
        sender: 'user',
        content: initialPrompt,
        timestamp: new Date().toISOString()
      };

      // Infer title from the initial prompt
      const inferredTitle = initialPrompt.length > 30
        ? `${initialPrompt.substring(0, 27)}...`
        : initialPrompt;

      // Update the chat with both the new message and the inferred title
      const updatedChat: Chat = {
        ...newChat,
        title: inferredTitle,
        messages: [newUserMessage],
        lastUpdated: new Date().toISOString()
      };

      // Replace the empty chat with the updated one
      setChats(prevChats =>
        prevChats.map(chat => chat.id === newChatId ? updatedChat : chat)
      );

      // Update the messages in the current view
      setMessages([newUserMessage]);
      setShowSuggestions(false);

      // Simulate AI response
      setTimeout(() => {
        const newAIMessage: ChatMessage = {
          id: `msg-${Date.now()}-ai`,
          sender: 'ai',
          content: `I'm processing your request about "${initialPrompt}". This is a placeholder response. In a real implementation, this would be generated by your AI service.`,
          timestamp: new Date().toISOString()
        };

        const updatedMessages = [...updatedChat.messages, newAIMessage];

        // Update messages in the UI
        setMessages(updatedMessages);

        // Update the chat in the chats array
        setChats(prevChats =>
          prevChats.map(chat =>
            chat.id === newChatId
              ? {
                  ...chat,
                  messages: updatedMessages,
                  lastUpdated: new Date().toISOString()
                }
              : chat
          )
        );
      }, 1000);
    }
  };

  // Function to handle selecting a chat
  const handleSelectChat = (chatId: string) => {
    setSelectedChatId(chatId);

    // Find the selected chat and load its messages
    const selectedChat = chats.find(chat => chat.id === chatId);
    if (selectedChat) {
      setMessages(selectedChat.messages);
      setShowSuggestions(selectedChat.messages.length === 0);
    } else {
      setMessages([]);
      setShowSuggestions(true);
    }
  };

  // Function to handle sending a new message
  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    // Create a new chat if none is selected
    if (!selectedChatId) {
      handleNewChat(inputValue);
      return;
    }

    // Add user message
    const newUserMessage: ChatMessage = {
      id: `msg-${Date.now()}-user`,
      sender: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };

    // Get the current chat
    const currentChat = chats.find(chat => chat.id === selectedChatId);
    if (!currentChat) return;

    // Check if this is the first message in the chat
    const isFirstMessage = currentChat.messages.length === 0;

    // Create updated messages array
    const updatedMessages = [...messages, newUserMessage];

    // Update UI immediately
    setMessages(updatedMessages);
    setInputValue('');
    setShowSuggestions(false);

    // If this is the first message, infer a title from it
    if (isFirstMessage) {
      // Create a title from the message content
      const inferredTitle = inputValue.length > 30
        ? `${inputValue.substring(0, 27)}...`
        : inputValue;

      // Update the chat with both the new message and the inferred title
      setChats(prevChats =>
        prevChats.map(chat =>
          chat.id === selectedChatId
            ? {
                ...chat,
                title: inferredTitle,
                messages: updatedMessages,
                lastUpdated: new Date().toISOString()
              }
            : chat
        )
      );
    } else {
      // Just update the messages if it's not the first message
      updateChatMessages(selectedChatId, updatedMessages);
    }

    // Simulate AI response (in a real app, this would call your AI service)
    setTimeout(() => {
      const newAIMessage: ChatMessage = {
        id: `msg-${Date.now()}-ai`,
        sender: 'ai',
        content: `I'm processing your request about "${inputValue}". This is a placeholder response. In a real implementation, this would be generated by your AI service.`,
        timestamp: new Date().toISOString()
      };

      const updatedWithAIMessage = [...updatedMessages, newAIMessage];
      setMessages(updatedWithAIMessage);

      // Update the chat in the chats array
      updateChatMessages(selectedChatId, updatedWithAIMessage);
    }, 1000);
  };

  // Function to handle selecting a suggested prompt
  const handleSelectPrompt = (prompt: string) => {
    setInputValue(prompt);
    // Optional: automatically send the message
    // if (!selectedChatId) {
    //   handleNewChat(prompt);
    // }
  };

  // No need to initialize with an empty chat
  // We'll create a chat only when the user starts typing or selects a prompt

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <SmartToyIcon color="primary" />
        <Typography level="title-md">{t('dashboard.maverick_ai', 'Maverick AI Assistant')}</Typography>
      </Box>

      {/* Chat selection header */}
      <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl sx={{ flex: 1 }}>
          <Select
            value={selectedChatId || ''}
            onChange={(_, value) => value && handleSelectChat(value as string)}
            size="sm"
            startDecorator={<ChatIcon />}
            placeholder={t('dashboard.select_chat', 'Select a chat')}
            disabled={chats.length === 0}
          >
            {chats.length === 0 ? (
              <Option value="" disabled>
                {t('dashboard.no_chats', 'No chats yet')}
              </Option>
            ) : (
              chats.filter(chat => chat.title).map((chat) => (
                <Option key={chat.id} value={chat.id}>
                  {chat.title}
                </Option>
              ))
            )}
          </Select>
        </FormControl>
        <IconButton
          variant="soft"
          color="primary"
          size="sm"
          onClick={() => handleNewChat()}
          title={t('dashboard.new_chat', 'New Chat')}
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* Chat messages */}
      <CardContent sx={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        gap: 2,
        bgcolor: 'background.level1'
      }}>
        {/* No chat selected - show welcome screen */}
        {!selectedChatId && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
            <SmartToyIcon color="primary" sx={{ fontSize: 48, opacity: 0.7 }} />
            <Typography level="body-md" color="neutral" textAlign="center">
              {t('dashboard.welcome_message', 'Welcome to Maverick AI Assistant')}
            </Typography>
            <Typography level="body-sm" color="neutral" textAlign="center">
              {t('dashboard.get_started', 'Ask a question or select a suggested prompt to get started')}
            </Typography>

            {/* Show suggested prompts in the welcome screen */}
            <Box sx={{ width: '100%', maxWidth: 400, mt: 2 }}>
              <Stack spacing={1}>
                {suggestedPrompts.map((prompt, index) => (
                  <Button
                    key={index}
                    variant="soft"
                    color="neutral"
                    size="sm"
                    endDecorator={<ArrowForwardIcon fontSize="small" />}
                    onClick={() => {
                      handleNewChat(prompt);
                    }}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  >
                    {prompt}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Box>
        )}

        {/* Empty chat with no suggestions - show start message */}
        {selectedChatId && messages.length === 0 && !showSuggestions && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography level="body-sm" color="neutral">
              {t('dashboard.start_conversation', 'Start a new conversation with Maverick AI')}
            </Typography>
          </Box>
        )}

        {/* Show messages and/or suggestions */}
        {selectedChatId && (messages.length > 0 || showSuggestions) && (
          <>
            {/* Show suggested prompts if explicitly shown */}
            {showSuggestions && (
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                  {t('dashboard.suggested_prompts', 'Try asking:')}
                </Typography>
                <Stack spacing={1}>
                  {suggestedPrompts.map((prompt, index) => (
                    <Button
                      key={index}
                      variant="soft"
                      color="neutral"
                      size="sm"
                      endDecorator={<ArrowForwardIcon fontSize="small" />}
                      onClick={() => handleSelectPrompt(prompt)}
                      sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    >
                      {prompt}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Message list */}
            <List sx={{ '--ListItem-paddingY': '0.5rem', '--ListItem-paddingX': '0rem' }}>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    display: 'flex',
                    flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 1
                  }}
                >
                  <Avatar
                    size="sm"
                    variant="soft"
                    color={message.sender === 'user' ? 'primary' : 'neutral'}
                  >
                    {message.sender === 'user' ? 'U' : 'AI'}
                  </Avatar>
                  <ListItemContent>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 'lg',
                        maxWidth: '80%',
                        bgcolor: message.sender === 'user' ? 'primary.softBg' : 'background.surface',
                        color: message.sender === 'user' ? 'primary.softColor' : 'text.primary',
                        ml: message.sender === 'user' ? 'auto' : 0,
                        mr: message.sender === 'user' ? 0 : 'auto',
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {message.content}
                    </Box>
                    <Typography
                      level="body-xs"
                      sx={{
                        mt: 0.5,
                        color: 'text.tertiary',
                        textAlign: message.sender === 'user' ? 'right' : 'left',
                        mx: 1
                      }}
                    >
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </ListItemContent>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>

      {/* Input area */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
        >
          <Stack direction="row" spacing={1}>
            <Input
              fullWidth
              placeholder={t('dashboard.ask_maverick', 'Ask Maverick something...')}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              endDecorator={
                <IconButton type="submit" disabled={!inputValue.trim()}>
                  <SendIcon />
                </IconButton>
              }
              // Create a new chat automatically when user starts typing in the input
              onFocus={() => {
                if (!selectedChatId && chats.length === 0) {
                  // Don't create a chat yet, wait for user to type and send
                  setShowSuggestions(true);
                }
              }}
            />
          </Stack>
        </form>
      </Box>
    </Card>
  );
};

export default MaverickAIChat;
